/**
 * Custom hook for managing avatars data
 */

import { useState, useEffect } from 'react';
import { getAvatars } from '../services/avatarService';
import type { Avatar } from '../services/avatarService';

interface UseAvatarsReturn {
  avatars: Avatar[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Custom hook to fetch and manage avatars data
 * @returns Object containing avatars data, loading state, error state, and refetch function
 */
export const useAvatars = (): UseAvatarsReturn => {
  const [avatars, setAvatars] = useState<Avatar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAvatars = async () => {
    try {
      console.log('🔄 [useAvatars] Starting to fetch avatars...');
      setLoading(true);
      setError(null);

      const response = await getAvatars();
      console.log('📥 [useAvatars] Response received from service:', {
        success: response.success,
        dataCount: response.data.length,
        message: response.message
      });

      if (response.success) {
        console.log('✅ [useAvatars] Avatars successfully processed:', response.data);
        setAvatars(response.data);
      } else {
        console.log('❌ [useAvatars] API returned error:', response.message);
        setError(response.message || 'Failed to load avatars');
      }
    } catch (err) {
      console.error('🚨 [useAvatars] Unexpected error:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
    } finally {
      console.log('🏁 [useAvatars] Fetch process completed, loading state set to false');
      setLoading(false);
    }
  };

  const refetch = async () => {
    await fetchAvatars();
  };

  useEffect(() => {
    fetchAvatars();
  }, []);

  return {
    avatars,
    loading,
    error,
    refetch
  };
};
